// ============================================================================
// Approver Incoming Requests — POST /tort/approver/time-off-requests
//
// Returns paginated, filterable, sortable time-off requests for the approver
// incoming requests view.
//
// Required:
//  - x-nuid (header)         → added automatically by the axios interceptor
//  - date   (body, YYYY-MM-DD)
//  - filters.dateOption      → "A" (all) | "R" (date range)
//  - filters.status          → array of status codes
//  - filters.requestType     → array of request-type codes
//
// Conditionally required:
//  - filters.startDate / filters.endDate → required when dateOption === "R"
//
// Optional:
//  - filters.department / filters.union / filters.approvalGroup / filters.jobCode
//  - page / perPage / sortBy / sortOrder
// ============================================================================

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../../api/axios'
import endpoints from '../../../api/endpoints'
import { extractApiErrorMessage } from '../../../utils/apiError'

// ---------------------------------------------------------------------------
// Request payload types
// ---------------------------------------------------------------------------

/** Date range option for the approver requests filter. */
export type ApproverDateOption = 'A' | 'R'

/**
 * Status codes accepted by the approver time-off-requests POST body.
 * These match the values in the RequestStatusCode enum.
 */
export type ApproverRequestStatusCode =
  | 'SUBMITTED'
  | 'APPROVED'
  | 'DENIED'
  | 'SUBMITTED_ONHOLD'
  | 'WITHDRAWN'
  | 'RTSUBMITTED'
  | 'RTFORWARDED'
  | 'RETRACTED'
  | 'RESUBMITTED'
  | 'RETURNED'

/** Time-off request type codes used by the approver requests filter. */
export type ApproverRequestTypeCode = 'V' | 'F' | 'D' | 'O' | 'A' | 'B'

/** Sortable field names accepted by the API. */
export type ApproverSortBy =
  | 'empNUID'
  | 'unionSeniority'
  | 'altSeniority'
  | 'rank'
  | 'dateRequested'
  | 'hoursRequested'
  | 'dateSubmitted'
  | 'shift'
  | 'requestType'
  | 'status'

/** Sort direction accepted by the API. */
export type ApproverSortOrder = 'ASC' | 'DESC'

/** Filters object nested inside the POST body. */
export interface ApproverRequestsFilters {
  /** Required: date range option. */
  dateOption: ApproverDateOption
  /** Required when dateOption === "R". Format: YYYY-MM-DD. */
  startDate?: string
  /** Required when dateOption === "R". Format: YYYY-MM-DD. */
  endDate?: string
  /** Required: array of status codes to include. */
  status: ApproverRequestStatusCode[]
  /** Required: array of request-type codes to include. */
  requestType: ApproverRequestTypeCode[]
  /** Optional: single department code extracted from the filter label. */
  department?: string[]
  /** Optional: single union code extracted from the filter label. */
  union?: string[]
  /** Optional: approval group ID extracted from the filter label. */
  approvalGroup?: string[]
  /** Optional: job code extracted from the filter label. */
  jobCode?: string[]
}

/**
 * Full POST body for POST /tort/approver/time-off-requests.
 * `page` and `perPage` are sent as strings per the API contract.
 */
export interface ApproverRequestsPayload {
  /** Reference date in YYYY-MM-DD format (typically today). */
  date: string
  filters?: ApproverRequestsFilters
  /** 1-based page number (sent as a string). */
  page?: string
  /** Page size (sent as a string). */
  perPage?: string
  sortBy?: ApproverSortBy
  sortOrder?: ApproverSortOrder
}

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

/** A single incoming time-off request item as returned by the API. */
export interface ApproverRequestItem {
  /** Display name including NUID, e.g. "Morgan, Jessie, Rose (X000233)". */
  employeeName: string
  /** Date(s) of the request, e.g. "06/30/2026" or "06/29/2026-07/01/2026". */
  toDate: string
  hoursRequested: string
  /** Human-readable type label, e.g. "Vacation (PTO/ETO)". */
  timeOffReqstType: string
  /** Human-readable status label, e.g. "Approved". */
  status: string
  /** Machine-readable status code, e.g. "APPROVED". Used for status icons. */
  statusCode: string
  /** ISO 8601 timestamp, e.g. "2026-06-23T07:01:43.364Z". */
  dateSubmitted: string
  detail: string
  /** Unique request ID used for navigation to the detail/process page. */
  requestId: string
  shift: string
  department: string
  union: string
  job: string
  unionSeniority: string
  /** Alternate seniority date or "NA". */
  altSeniority: string
  rank: string
  approverName: string
  comments: string
}

export interface ApproverRequestsMeta {
  pageNumber: number
  pageSize: number
  /** Total number of matching records across all pages. */
  total: number
  totalPages: number
}

/** Full response shape from POST /tort/approver/time-off-requests. */
export interface ApproverRequestsApiResponse {
  numRows: number
  items: ApproverRequestItem[]
  meta: ApproverRequestsMeta
}

// ---------------------------------------------------------------------------
// Default payload values (sent when no specific filter selection is made)
// ---------------------------------------------------------------------------

/**
 * All status codes included when no specific status filter is selected.
 * Maps to every entry in the RequestStatusCode enum.
 */
export const DEFAULT_APPROVER_STATUS_CODES: ApproverRequestStatusCode[] = [
  'SUBMITTED',
  'RTSUBMITTED',
  'RESUBMITTED',
]

/**
 * All request-type codes included when no specific type filter is selected.
 * V=Vacation, F=Float, D=FPD, O=Other, A=Vacation Bid, B=Birthday.
 */
export const DEFAULT_APPROVER_REQUEST_TYPE_CODES: ApproverRequestTypeCode[] = [
  'V',
  'F',
  'D',
  'O',
  'A',
  'B',
]

// ---------------------------------------------------------------------------
// Redux state
// ---------------------------------------------------------------------------

export interface ApproverRequestsState {
  items: ApproverRequestItem[]
  loading: boolean
  error: string | null
  currentPage: number
  pageSize: number
  /** Total records across all pages (from meta.total). */
  totalRecords: number
  totalPages: number
  sortBy: ApproverSortBy | undefined
  sortOrder: ApproverSortOrder
  lastPayload?: ApproverRequestsPayload
}

const initialState: ApproverRequestsState = {
  items: [],
  loading: false,
  error: null,
  currentPage: 1,
  pageSize: 20,
  totalRecords: 0,
  totalPages: 0,
  sortBy: undefined,
  sortOrder: 'ASC',
  lastPayload: undefined,
}

const fetchApproverRequestsApi = async (
  payload: ApproverRequestsPayload,
  signal?: AbortSignal
): Promise<ApproverRequestsApiResponse> => {
  const response = await api.post(
    endpoints.approver.requests,
    payload,
    { signal }
  )

  return response.data as ApproverRequestsApiResponse
}

// ---------------------------------------------------------------------------
// Thunk
// ---------------------------------------------------------------------------

/**
 * Fetches incoming time-off requests for the approver with pagination,
 * optional filtering, and optional sorting.
 *
 * Dispatches pending → fulfilled | rejected lifecycle actions that update
 * `loading`, `items`, pagination state, and `error` in the slice.
 *
 * The `x-nuid` header is attached automatically by the axios request
 * interceptor — no manual header work is required at the call site.
 *
 * @param payload - Full POST body per the API contract.
 */
export const getApproverRequests = createAsyncThunk(
  'approverRequests/getApproverRequests',
  async (payload: ApproverRequestsPayload, { rejectWithValue, signal }) => {
    try {
      const data = await fetchApproverRequestsApi(payload, signal)

      return {
        items: data.items ?? [],
        currentPage: data.meta?.pageNumber ?? 1,
        pageSize: data.meta?.pageSize ?? 20,
        totalRecords: data.meta?.total ?? 0,
        totalPages: data.meta?.totalPages ?? 0,
      }
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(
          error,
          'Failed to fetch approver time-off requests'
        )
      )
    }
  }
)

export const getApproverPrintRequests = createAsyncThunk(
  'approverRequests/getApproverPrintRequests',
  async (payload: ApproverRequestsPayload) => {
    const response = await api.post(
      endpoints.approver.requests,
      payload
    )

    return response.data as ApproverRequestsApiResponse
  }
)

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const approverRequestsSlice = createSlice({
  name: 'approverRequests',
  initialState,
  reducers: {
    /**
     * Update the active sort configuration.
     * @param action.payload.sortBy    - Column to sort by (undefined = none).
     * @param action.payload.sortOrder - ASC or DESC.
     */
    setApproverSort: (
      state,
      action: {
        payload: { sortBy?: ApproverSortBy; sortOrder: ApproverSortOrder }
      }
    ) => {
      state.sortBy = action.payload.sortBy
      state.sortOrder = action.payload.sortOrder
    },
    /** Reset state to initial values (e.g., on logout or view unmount). */
    clearApproverRequests: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getApproverRequests.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getApproverRequests.fulfilled, (state, action) => {
      state.loading = false
      state.items = action.payload.items
      state.currentPage = action.payload.currentPage
      state.pageSize = action.payload.pageSize
      state.totalRecords = action.payload.totalRecords
      state.totalPages = action.payload.totalPages
      state.lastPayload = action.meta.arg
      state.error = null
    })
    builder.addCase(getApproverRequests.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload as string
    })
  },
})

export const { setApproverSort, clearApproverRequests } =
  approverRequestsSlice.actions
export default approverRequestsSlice.reducer
